import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  CardHeader,
  Combobox,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Spinner,
  Switch,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  tokens,
} from '@fluentui/react-components';
import {
  AddRegular,
  ArrowSyncCircleRegular,
  CheckmarkCircleRegular,
  DeleteRegular,
  ErrorCircleRegular,
  SaveRegular,
  SearchRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync, type AsyncState } from '../api/useAsync';
import type { ApiClient } from '../api/client';
import { PageHeader } from '../components/PageHeader';
import { useSession } from '../auth/SessionProvider';
import './governance.css';
import type {
  AuScopeAssignmentDto,
  DirectoryUserDto,
  IamAppDto,
  NamingPrefixDto,
  NamingScope,
  PagePolicyDto,
  PimGovernanceSettingsDto,
  RequirementMode,
  TeamTypeDto,
} from '../api/types';

const PAGE_LABELS: Record<string, string> = {
  'users.create': 'Benutzer anlegen',
  'users.update': 'Benutzer-Attribute ändern',
  'users.block': 'Benutzer sperren',
  'users.delete': 'Benutzer löschen',
  'users.credentials': 'Passwort / MFA / TAP',
  'groups.create': 'Gruppe erstellen',
  'groups.members': 'Mitglieder ändern',
  'groups.delete': 'Gruppe löschen',
  'teams.create': 'Team erstellen',
  'teams.members': 'Team-Mitglieder ändern',
  'teams.delete': 'Team löschen',
  'guests.create': 'Gast einladen',
  'guests.edit': 'Gast-Gültigkeit ändern',
  'guests.revoke': 'Gast-Einladung widerrufen',
  'devices.laps': 'LAPS',
  'devices.software': 'Software-Zuweisung',
  'access.iam': 'IAM-Liste',
  'access.apps': 'App-Zuweisungen',
  'pim.view': 'PIM-Ansicht',
  'pim.remove': 'PIM-Zuweisung entfernen',
};

const MODE_LABELS: Record<RequirementMode, string> = { None: 'Keine', Optional: 'Optional', Required: 'Pflicht' };
const MODES: RequirementMode[] = ['None', 'Optional', 'Required'];
const MODE_OPTIONS = MODES.map((m) => ({ value: m, label: MODE_LABELS[m] }));
const SCOPE_LABELS: Record<NamingScope, string> = {
  Team: 'Teams',
  SecurityGroup: 'Sec-Group',
  M365Group: 'M365-Group',
};
const SCOPE_COLOR: Record<NamingScope, 'brand' | 'informative' | 'success'> = {
  Team: 'success',
  SecurityGroup: 'informative',
  M365Group: 'brand',
};

type GovTab = 'policies' | 'naming' | 'teams' | 'aus' | 'pim' | 'iamapps';

export function GovernancePage() {
  const api = useApi();
  const { me } = useSession();
  const policies = useAsync(() => api.getPagePolicies(), [api]);
  const prefixes = useAsync(() => api.getNamingPrefixes(), [api]);
  const [tab, setTab] = useState<GovTab>('policies');

  if (me && !me.isAdmin) {
    return (
      <div>
        <PageHeader title="Governance" />
        <MessageBar intent="warning">
          <MessageBarBody>Governance-Einstellungen sind Administratoren vorbehalten.</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Governance"
        description="Freigaben, Ticketpflicht und Namenskonventionen zentral je Seite festlegen."
      />
      <div className="gov-tabs-sticky">
        <TabList selectedValue={tab} onTabSelect={(_, d) => setTab(d.value as GovTab)}>
          <Tab value="policies">Freigaben &amp; Tickets</Tab>
          <Tab value="naming">Namenskonventionen</Tab>
          <Tab value="teams">Team-Arten</Tab>
          <Tab value="aus">Administrative Units</Tab>
          <Tab value="pim">PIM</Tab>
          <Tab value="iamapps">IAM-Apps</Tab>
        </TabList>
      </div>
      <div className="gov-tab-body">
        {tab === 'policies' ? <PolicySection api={api} state={policies} /> : null}
        {tab === 'naming' ? <PrefixSection api={api} state={prefixes} /> : null}
        {tab === 'teams' ? <TeamTypeSection api={api} /> : null}
        {tab === 'aus' ? <AuScopeSection api={api} /> : null}
        {tab === 'pim' ? <PimSettingsSection api={api} /> : null}
        {tab === 'iamapps' ? <IamAppsSection api={api} /> : null}
      </div>
    </div>
  );
}

function Segmented({
  value,
  onChange,
  ariaLabel,
}: {
  value: RequirementMode;
  onChange: (mode: RequirementMode) => void;
  ariaLabel?: string;
}) {
  return (
    <div className="seg" role="radiogroup" aria-label={ariaLabel}>
      {MODE_OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          className={o.value === value ? 'seg-item selected' : 'seg-item'}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function PolicySection({ api, state }: { api: ApiClient; state: AsyncState<PagePolicyDto[]> }) {
  const [rows, setRows] = useState<PagePolicyDto[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (state.data) setRows(state.data);
  }, [state.data]);

  async function save(policy: PagePolicyDto, patch: Partial<PagePolicyDto>) {
    const updated = { ...policy, ...patch };
    setRows((rs) => rs.map((r) => (r.pageKey === policy.pageKey ? updated : r)));
    try {
      await api.updatePagePolicy(policy.pageKey, {
        approvalMode: updated.approvalMode,
        ticketMode: updated.ticketMode,
        approverGroupId: updated.approverGroupId,
      });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Card>
      <CardHeader
        header={<Text weight="semibold">Freigaben &amp; Ticketpflicht je Seite</Text>}
        description={<Text size={200}>Keine / optional / Pflicht je Aktion — Änderungen werden sofort gespeichert.</Text>}
      />
      {state.loading ? <Spinner size="tiny" label="Lade…" /> : null}
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}
      {rows.length > 0 ? (
        <div className="gov-policy-grid">
          <span className="gov-col-head">Seite</span>
          <span className="gov-col-head">Freigabe</span>
          <span className="gov-col-head">Ticket</span>
          {rows.map((p, i) => (
            <Fragment key={p.pageKey}>
              {i > 0 ? <span className="gov-sep" /> : null}
              <span className="gov-policy-name">{PAGE_LABELS[p.pageKey] ?? p.pageKey}</span>
              <Segmented
                ariaLabel={`Freigabe: ${PAGE_LABELS[p.pageKey] ?? p.pageKey}`}
                value={p.approvalMode}
                onChange={(m) => save(p, { approvalMode: m })}
              />
              <Segmented
                ariaLabel={`Ticket: ${PAGE_LABELS[p.pageKey] ?? p.pageKey}`}
                value={p.ticketMode}
                onChange={(m) => save(p, { ticketMode: m })}
              />
            </Fragment>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

function PrefixSection({ api, state }: { api: ApiClient; state: AsyncState<NamingPrefixDto[]> }) {
  const [rows, setRows] = useState<NamingPrefixDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [newScope, setNewScope] = useState<NamingScope>('SecurityGroup');
  const [newValue, setNewValue] = useState('');
  const [newDesc, setNewDesc] = useState('');

  useEffect(() => {
    if (state.data) setRows(state.data);
  }, [state.data]);

  async function reload() {
    try {
      setRows(await api.getNamingPrefixes());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function patch(id: string, p: Partial<NamingPrefixDto>) {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...p } : r)));
  }

  async function saveRow(row: NamingPrefixDto) {
    try {
      await api.updateNamingPrefix(row.id, {
        scope: row.scope,
        value: row.value,
        description: row.description,
        enabled: row.enabled,
      });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function add() {
    if (!newValue.trim()) return;
    try {
      await api.createNamingPrefix({
        scope: newScope,
        value: newValue.trim(),
        description: newDesc.trim() || undefined,
        enabled: true,
      });
      setNewValue('');
      setNewDesc('');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function remove(id: string) {
    try {
      await api.deleteNamingPrefix(id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Card>
      <CardHeader
        header={<Text weight="semibold">Namenskonventionen (Präfixe)</Text>}
        description={<Text size={200}>Präfixe für die Gruppen-Erstellung (Teams siehe Reiter „Team-Arten").</Text>}
      />
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}
      {state.loading ? <Spinner size="tiny" label="Lade…" /> : null}

      <Table size="small" aria-label="Namenskonventionen">
        <TableHeader>
          <TableRow>
            <TableHeaderCell>Bereich</TableHeaderCell>
            <TableHeaderCell>Präfix</TableHeaderCell>
            <TableHeaderCell>Beschreibung</TableHeaderCell>
            <TableHeaderCell>Aktiv</TableHeaderCell>
            <TableHeaderCell>Aktionen</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows
            .filter((r) => r.scope !== 'Team')
            .map((row) => (
            <TableRow key={row.id}>
              <TableCell>
                <Badge appearance="tint" color={SCOPE_COLOR[row.scope]}>
                  {SCOPE_LABELS[row.scope]}
                </Badge>
              </TableCell>
              <TableCell>
                <Input
                  size="small"
                  value={row.value}
                  onChange={(_, d) => patch(row.id, { value: d.value })}
                  style={{ width: '110px' }}
                />
              </TableCell>
              <TableCell>
                <Input
                  size="small"
                  value={row.description ?? ''}
                  onChange={(_, d) => patch(row.id, { description: d.value })}
                  style={{ width: '260px' }}
                />
              </TableCell>
              <TableCell>
                <Switch checked={row.enabled} onChange={(_, d) => patch(row.id, { enabled: d.checked })} />
              </TableCell>
              <TableCell>
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                  <Button size="small" icon={<SaveRegular />} onClick={() => saveRow(row)}>
                    Speichern
                  </Button>
                  <Button size="small" appearance="subtle" icon={<DeleteRegular />} onClick={() => remove(row.id)} />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: tokens.spacingHorizontalS,
          marginTop: tokens.spacingVerticalM,
          flexWrap: 'wrap',
        }}
      >
        <Dropdown
          size="small"
          style={{ minWidth: '120px' }}
          value={SCOPE_LABELS[newScope]}
          selectedOptions={[newScope]}
          onOptionSelect={(_, d) => d.optionValue && setNewScope(d.optionValue as NamingScope)}
        >
          <Option value="SecurityGroup" text="Sec-Group">
            Sec-Group
          </Option>
          <Option value="M365Group" text="M365-Group">
            M365-Group
          </Option>
        </Dropdown>
        <Input size="small" placeholder="Präfix (z. B. GRP-)" value={newValue} onChange={(_, d) => setNewValue(d.value)} />
        <Input
          size="small"
          placeholder="Beschreibung"
          value={newDesc}
          onChange={(_, d) => setNewDesc(d.value)}
          style={{ width: '260px' }}
        />
        <Button size="small" appearance="primary" icon={<AddRegular />} onClick={add}>
          Hinzufügen
        </Button>
      </div>
    </Card>
  );
}

function TeamTypeSection({ api }: { api: ApiClient }) {
  const state = useAsync(() => api.getTeamTypes(), [api]);
  const [rows, setRows] = useState<TeamTypeDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [nName, setNName] = useState('');
  const [nPrefix, setNPrefix] = useState('');
  const [nDesc, setNDesc] = useState('');
  const [nGuests, setNGuests] = useState(false);

  useEffect(() => {
    if (state.data) setRows(state.data);
  }, [state.data]);

  async function reload() {
    try {
      setRows(await api.getTeamTypes());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  function patch(id: string, p: Partial<TeamTypeDto>) {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...p } : r)));
  }

  async function saveRow(row: TeamTypeDto) {
    try {
      await api.updateTeamType(row.id, {
        name: row.name,
        description: row.description,
        prefix: row.prefix,
        allowGuests: row.allowGuests,
        enabled: row.enabled,
      });
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function add() {
    if (!nName.trim()) return;
    try {
      await api.createTeamType({
        name: nName.trim(),
        description: nDesc.trim() || undefined,
        prefix: nPrefix.trim(),
        allowGuests: nGuests,
        enabled: true,
      });
      setNName('');
      setNPrefix('');
      setNDesc('');
      setNGuests(false);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function remove(id: string) {
    try {
      await api.deleteTeamType(id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <Card>
      <CardHeader
        header={<Text weight="semibold">Team-Arten</Text>}
        description={<Text size={200}>Namenskonvention und Gast-Policy je Team-Art — gilt bei „Team erstellen".</Text>}
      />
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}
      {state.loading ? <Spinner size="tiny" label="Lade…" /> : null}

      <div className="tt-grid">
        <div className="tt-head">
          <span>Art</span>
          <span>Präfix</span>
          <span>Beschreibung</span>
          <span>Gäste erlaubt</span>
          <span>Aktiv</span>
          <span style={{ textAlign: 'right' }}>Aktionen</span>
        </div>
        {rows.map((row) => (
          <div className="tt-row" key={row.id}>
            <Input size="small" value={row.name} onChange={(_, d) => patch(row.id, { name: d.value })} style={{ width: '100%' }} />
            <Input size="small" value={row.prefix} onChange={(_, d) => patch(row.id, { prefix: d.value })} style={{ width: '100%' }} />
            <Input
              size="small"
              value={row.description ?? ''}
              onChange={(_, d) => patch(row.id, { description: d.value })}
              style={{ width: '100%' }}
            />
            <Switch checked={row.allowGuests} onChange={(_, d) => patch(row.id, { allowGuests: d.checked })} />
            <Switch checked={row.enabled} onChange={(_, d) => patch(row.id, { enabled: d.checked })} />
            <div className="tt-actions">
              <Button size="small" icon={<SaveRegular />} onClick={() => saveRow(row)}>
                Speichern
              </Button>
              <Button size="small" appearance="subtle" icon={<DeleteRegular />} onClick={() => remove(row.id)} />
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: tokens.spacingHorizontalS,
          marginTop: tokens.spacingVerticalM,
          flexWrap: 'wrap',
        }}
      >
        <Input size="small" placeholder="Art (z. B. intern)" value={nName} onChange={(_, d) => setNName(d.value)} />
        <Input size="small" placeholder="Präfix (z. B. INT-)" value={nPrefix} onChange={(_, d) => setNPrefix(d.value)} style={{ width: '110px' }} />
        <Input
          size="small"
          placeholder="Beschreibung"
          value={nDesc}
          onChange={(_, d) => setNDesc(d.value)}
          style={{ width: '240px' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Switch checked={nGuests} onChange={(_, d) => setNGuests(d.checked)} />
          <Caption1>Gäste</Caption1>
        </div>
        <Button size="small" appearance="primary" icon={<AddRegular />} onClick={add}>
          Hinzufügen
        </Button>
      </div>
    </Card>
  );
}

function AuScopeSection({ api }: { api: ApiClient }) {
  const aus = useAsync(() => api.getAdministrativeUnits(), [api]);
  const scope = useAsync(() => api.getAuScope(), [api]);
  const [rows, setRows] = useState<AuScopeAssignmentDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [auId, setAuId] = useState('');
  const [userText, setUserText] = useState('');
  const [userResults, setUserResults] = useState<DirectoryUserDto[]>([]);
  const [selectedUser, setSelectedUser] = useState<DirectoryUserDto | undefined>();
  const searchTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (scope.data) setRows(scope.data);
  }, [scope.data]);

  function onUserInput(text: string) {
    setUserText(text);
    setSelectedUser(undefined);
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    if (text.trim().length < 2) {
      setUserResults([]);
      return;
    }
    searchTimer.current = window.setTimeout(() => {
      api
        .searchUsers(text.trim())
        .then(setUserResults)
        .catch(() => setUserResults([]));
    }, 300);
  }

  async function reload() {
    try {
      setRows(await api.getAuScope());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function assign() {
    const au = aus.data?.find((a) => a.id === auId);
    if (!selectedUser || !au) return;
    try {
      await api.createAuScope({
        userObjectId: selectedUser.id,
        userUpn: selectedUser.userPrincipalName,
        administrativeUnitId: au.id,
        auDisplayName: au.displayName,
      });
      setSelectedUser(undefined);
      setUserText('');
      setUserResults([]);
      setAuId('');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function remove(id: string) {
    try {
      await api.deleteAuScope(id);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const selectedAuName = aus.data?.find((a) => a.id === auId)?.displayName ?? '';

  return (
    <Card>
      <CardHeader
        header={<Text weight="semibold">Bereich (Administrative Units)</Text>}
        description={
          <Text size={200}>
            App-berechtigte Nutzer auf AUs eingrenzen — gescopte Nutzer sehen in der Personensuche nur ihre AU-Mitglieder.
          </Text>
        }
      />

      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      {aus.loading ? <Spinner size="tiny" label="Lade AUs…" /> : null}
      {aus.data && aus.data.length === 0 ? (
        <Body1>Im Tenant sind keine Administrative Units definiert.</Body1>
      ) : null}
      {aus.data && aus.data.length > 0 ? (
        <div style={{ marginBottom: tokens.spacingVerticalM }}>
          <Caption1>Verfügbare AUs:</Caption1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS, marginTop: '4px' }}>
            {aus.data.map((a) => (
              <Badge key={a.id} appearance="outline">
                {a.displayName}
              </Badge>
            ))}
          </div>
        </div>
      ) : null}

      {rows.length > 0 ? (
        <Table size="small" aria-label="AU-Zuweisungen">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Nutzer</TableHeaderCell>
              <TableHeaderCell>Administrative Unit</TableHeaderCell>
              <TableHeaderCell>Aktion</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>
                  <span className="pii">{r.userUpn}</span>
                </TableCell>
                <TableCell>{r.auDisplayName}</TableCell>
                <TableCell>
                  <Button size="small" appearance="subtle" icon={<DeleteRegular />} onClick={() => remove(r.id)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <Body1>Noch keine Zuweisungen.</Body1>
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: tokens.spacingHorizontalS,
          marginTop: tokens.spacingVerticalM,
          flexWrap: 'wrap',
        }}
      >
        <Field label="Nutzer">
          <Combobox
            freeform
            placeholder="Person suchen…"
            value={userText}
            selectedOptions={selectedUser ? [selectedUser.id] : []}
            onInput={(ev) => onUserInput((ev.target as HTMLInputElement).value)}
            onOptionSelect={(_, d) => {
              const u = userResults.find((r) => r.id === d.optionValue);
              if (u) {
                setSelectedUser(u);
                setUserText(u.displayName);
              }
            }}
          >
            {userResults.map((u) => (
              <Option key={u.id} value={u.id} text={u.displayName}>
                <span className="pii">
                  {u.displayName} · {u.userPrincipalName}
                </span>
              </Option>
            ))}
          </Combobox>
        </Field>
        <Field label="Administrative Unit">
          <Dropdown
            placeholder="AU wählen"
            style={{ minWidth: '200px' }}
            value={selectedAuName}
            selectedOptions={auId ? [auId] : []}
            onOptionSelect={(_, d) => d.optionValue && setAuId(d.optionValue)}
          >
            {(aus.data ?? []).map((a) => (
              <Option key={a.id} value={a.id} text={a.displayName}>
                {a.displayName}
              </Option>
            ))}
          </Dropdown>
        </Field>
        <Button appearance="primary" icon={<AddRegular />} disabled={!selectedUser || !auId} onClick={assign}>
          Zuweisen
        </Button>
      </div>
    </Card>
  );
}

function TierRow({
  icon,
  color,
  label,
  hint,
  value,
  onChange,
}: {
  icon: ReactNode;
  color: string;
  label: string;
  hint: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color, minWidth: '200px' }}>
        {icon}
        {label}
      </span>
      <Input
        type="number"
        value={String(value)}
        onChange={(_, d) => onChange(Math.max(0, Number(d.value.replace(/[^0-9]/g, '')) || 0))}
        style={{ width: '90px' }}
      />
      <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{hint}</Caption1>
    </div>
  );
}

function ThreshRow({
  label,
  warn,
  danger,
  onWarn,
  onDanger,
}: {
  label: string;
  warn: number;
  danger: number;
  onWarn: (v: number) => void;
  onDanger: (v: number) => void;
}) {
  const num = (v: string) => Math.max(0, Number(v.replace(/[^0-9]/g, '')) || 0);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' }}>
      <span style={{ minWidth: '110px', fontWeight: 600 }}>{label}</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: tokens.colorStatusWarningForeground1 }}>
        gelb ≥
        <Input type="number" value={String(warn)} onChange={(_, d) => onWarn(num(d.value))} style={{ width: '72px' }} />
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: tokens.colorStatusDangerForeground1 }}>
        rot ≥
        <Input type="number" value={String(danger)} onChange={(_, d) => onDanger(num(d.value))} style={{ width: '72px' }} />
      </span>
    </div>
  );
}

function PimSettingsSection({ api }: { api: ApiClient }) {
  const state = useAsync(() => api.getPimSettings(), [api]);
  const [s, setS] = useState<PimGovernanceSettingsDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (state.data) setS(state.data);
  }, [state.data]);

  function patch(p: Partial<PimGovernanceSettingsDto>) {
    setS((cur) => (cur ? { ...cur, ...p } : cur));
    setSaved(false);
  }

  async function save() {
    if (!s) return;
    try {
      const res = await api.updatePimSettings(s);
      setS(res);
      setSaved(true);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const num = (v: string) => Math.max(0, Number(v.replace(/[^0-9]/g, '')) || 0);

  return (
    <Card>
      <CardHeader
        header={<Text weight="semibold">PIM-Nutzungsbewertung</Text>}
        description={
          <Text size={200}>
            Schwellen für die Häufigkeit (Aktivierungen pro Jahr) und die „stale"-Markierung — Piktogramm von problemlos
            bis Gefahr. Gilt für die PIM-Seite (Danger-Zone).
          </Text>
        }
      />
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}
      {state.loading || !s ? (
        <Spinner size="tiny" label="Lade…" />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, maxWidth: '640px' }}>
          <TierRow
            icon={<CheckmarkCircleRegular />}
            color={tokens.colorStatusSuccessForeground1}
            label="häufig (problemlos)"
            hint="ab … Aktivierungen / Jahr"
            value={s.frequentMinPerYear}
            onChange={(v) => patch({ frequentMinPerYear: v })}
          />
          <TierRow
            icon={<ArrowSyncCircleRegular />}
            color={tokens.colorBrandForeground1}
            label="regelmäßig"
            hint="ab … / Jahr"
            value={s.regularMinPerYear}
            onChange={(v) => patch({ regularMinPerYear: v })}
          />
          <TierRow
            icon={<WarningRegular />}
            color={tokens.colorStatusWarningForeground1}
            label="selten"
            hint="ab … / Jahr"
            value={s.rareMinPerYear}
            onChange={(v) => patch({ rareMinPerYear: v })}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: tokens.spacingHorizontalS,
              color: tokens.colorStatusDangerForeground1,
            }}
          >
            <ErrorCircleRegular />
            <Text>nie (Gefahr) — darunter bzw. keine Aktivierung</Text>
          </div>

          <div style={{ borderTop: `1px solid ${tokens.colorNeutralStroke2}`, paddingTop: tokens.spacingVerticalM }}>
            <Field label={'„Stale" ab Tagen ohne Aktivierung'}>
              <Input
                type="number"
                value={String(s.staleDays)}
                onChange={(_, d) => patch({ staleDays: Math.max(1, num(d.value)) })}
                style={{ width: '120px' }}
              />
            </Field>
          </div>

          <div
            style={{
              borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
              paddingTop: tokens.spacingVerticalM,
              display: 'flex',
              flexDirection: 'column',
              gap: tokens.spacingVerticalS,
            }}
          >
            <Text weight="semibold">Zähler-Schwellen (Chip-Farben in der Rollen-Übersicht)</Text>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              Wert ≥ „rot" → roter Chip, ≥ „gelb" → gelber Chip, sonst grün.
            </Caption1>
            <ThreshRow
              label="Count"
              warn={s.countWarn}
              danger={s.countDanger}
              onWarn={(v) => patch({ countWarn: v })}
              onDanger={(v) => patch({ countDanger: v })}
            />
            <ThreshRow
              label="Aktiv"
              warn={s.activeWarn}
              danger={s.activeDanger}
              onWarn={(v) => patch({ activeWarn: v })}
              onDanger={(v) => patch({ activeDanger: v })}
            />
            <ThreshRow
              label="Eligible"
              warn={s.eligibleWarn}
              danger={s.eligibleDanger}
              onWarn={(v) => patch({ eligibleWarn: v })}
              onDanger={(v) => patch({ eligibleDanger: v })}
            />
            <ThreshRow
              label="Permanent"
              warn={s.permanentWarn}
              danger={s.permanentDanger}
              onWarn={(v) => patch({ permanentWarn: v })}
              onDanger={(v) => patch({ permanentDanger: v })}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
            <Button appearance="primary" icon={<SaveRegular />} onClick={save}>
              Speichern
            </Button>
            {saved ? <Caption1 style={{ color: tokens.colorStatusSuccessForeground1 }}>Gespeichert.</Caption1> : null}
          </div>
        </div>
      )}
    </Card>
  );
}

type AppSortCol = 'app' | 'display' | 'usage';

function IamAppsSection({ api }: { api: ApiClient }) {
  const [rows, setRows] = useState<IamAppDto[]>([]);
  const [edits, setEdits] = useState<Record<string, { displayName: string; description: string }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ col: AppSortCol; dir: 'asc' | 'desc' }>({ col: 'app', dir: 'asc' });

  useEffect(() => {
    setLoading(true);
    api
      .getIamApps()
      .then((r) => {
        setRows(r);
        setEdits(Object.fromEntries(r.map((a) => [a.appKey, { displayName: a.displayName ?? '', description: a.description ?? '' }])));
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [api]);

  const view = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? rows.filter(
          (r) =>
            r.appKey.toLowerCase().includes(q) ||
            (r.displayName ?? '').toLowerCase().includes(q) ||
            (r.description ?? '').toLowerCase().includes(q),
        )
      : rows;
    const val = (r: IamAppDto): string | number =>
      sort.col === 'usage' ? r.usageCount : sort.col === 'display' ? (r.displayName ?? '').toLowerCase() : r.appKey.toLowerCase();
    return [...filtered].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb));
      return sort.dir === 'asc' ? c : -c;
    });
  }, [rows, search, sort]);

  function setEdit(appKey: string, patch: Partial<{ displayName: string; description: string }>) {
    setEdits((e) => ({ ...e, [appKey]: { ...e[appKey], ...patch } }));
  }

  async function save(appKey: string) {
    const e = edits[appKey];
    const row = rows.find((r) => r.appKey === appKey);
    if (!e || !row) return;
    if (e.displayName === (row.displayName ?? '') && e.description === (row.description ?? '')) return;
    setError(null);
    try {
      const upd = await api.upsertIamApp({
        appKey,
        displayName: e.displayName.trim() || undefined,
        description: e.description.trim() || undefined,
      });
      setRows((rs) => rs.map((r) => (r.appKey === appKey ? { ...r, displayName: upd.displayName, description: upd.description } : r)));
      setSavedKey(appKey);
      window.setTimeout(() => setSavedKey((k) => (k === appKey ? null : k)), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  function sortable(col: AppSortCol): { sortable: true; sortDirection: 'ascending' | 'descending' | undefined; onClick: () => void } {
    return {
      sortable: true,
      sortDirection: sort.col === col ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined,
      onClick: () => setSort((s) => (s.col === col ? { col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' })),
    };
  }

  if (loading) return <Spinner label="Lade IAM-Apps…" />;

  return (
    <div>
      <Body1 style={{ display: 'block', marginBottom: 12, color: tokens.colorNeutralForeground2 }}>
        Alle in Rollen verwendeten Apps. Der hier gesetzte <strong>Anzeigename</strong> erscheint in den Rollen und in der Heatmap;
        die Beschreibung ist eine freie Notiz.
      </Body1>
      {error ? (
        <MessageBar intent="error" style={{ marginBottom: 12 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}

      <Input
        contentBefore={<SearchRegular />}
        placeholder="App, Anzeigename oder Beschreibung suchen…"
        value={search}
        onChange={(_, d) => setSearch(d.value)}
        style={{ width: 320, marginBottom: 12 }}
      />

      {rows.length === 0 ? (
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Noch keine in Rollen verwendeten Apps.</Caption1>
      ) : (
        <Table size="small" aria-label="IAM-Apps">
          <TableHeader>
            <TableRow>
              <TableHeaderCell {...sortable('app')}>App-Name</TableHeaderCell>
              <TableHeaderCell {...sortable('display')}>Anzeigename</TableHeaderCell>
              <TableHeaderCell>Beschreibung</TableHeaderCell>
              <TableHeaderCell {...sortable('usage')} style={{ maxWidth: 110 }}>Verwendet</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {view.map((r) => (
              <TableRow key={r.appKey}>
                <TableCell className="pii" style={{ fontWeight: 500 }}>{r.appKey}</TableCell>
                <TableCell>
                  <Input
                    size="small"
                    placeholder={r.appKey}
                    value={edits[r.appKey]?.displayName ?? ''}
                    onChange={(_, d) => setEdit(r.appKey, { displayName: d.value })}
                    onBlur={() => save(r.appKey)}
                    style={{ width: '100%' }}
                  />
                </TableCell>
                <TableCell>
                  <Input
                    size="small"
                    placeholder="Notiz…"
                    value={edits[r.appKey]?.description ?? ''}
                    onChange={(_, d) => setEdit(r.appKey, { description: d.value })}
                    onBlur={() => save(r.appKey)}
                    style={{ width: '100%' }}
                  />
                </TableCell>
                <TableCell style={{ maxWidth: 110 }}>
                  {savedKey === r.appKey ? (
                    <Caption1 style={{ color: tokens.colorStatusSuccessForeground1 }}>Gespeichert</Caption1>
                  ) : (
                    <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>{r.usageCount}× zugewiesen</Caption1>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
